import React, { useContext, useEffect, useState } from "react";
import { Box, Stack, Button, Typography, TableContainer } from "@mui/material";
import useReports from "../../../hooks/useReports";
import DatePickerComponent from "../../../components/atoms/DatePickerComponent";
import SelectComponent from "../../../components/atoms/Select";
import DoughnutChart from "../../../components/atoms/DoughnutChart";
import CardComponent from "../../../components/atoms/CardComponent";
import ButtonComponent from "../../../components/atoms/ButtonComponent";
import TableLine from "../../../components/atoms/TableLine";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { TablePagination } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import useWexClient from "../../../hooks/Clients/wexClient,";
import { AuthContext } from "../../../Auth/Context";
import { useParams } from "react-router-dom";

const ReportesTab = () => {
  const { data, fetchFoliosReport } = useReports();
  const { id } = useParams();
  const { payload } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { getRegions } = useWexClient();
  // Permisos
  const ROLE = payload?.profile?.class;
  const isAdmin = ROLE === "_ADMIN_";

  // 🔹 Estados de filtros
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [region, setRegion] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);
  // Paginacion
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // 🔹 Columnas
  const columns = [
    "Folio Externo",
    "Folio",
    "Status",
    "Ajustador",
    "Region",
    "Clasificacion",
    "Comentarios",
    "Tiempo de Espera",
    "Tiempo de Atencion",
    "Tiempo Total",
    "Fecha Creacion",
  ];

  // 🔹 Cargar regiones si es ADMIN
  useEffect(() => {
    const loadRegions = async () => {
      if (isAdmin) {
        try {
          const body = {
            idService: id,
          };

          const response = await getRegions(body); // ✅ await aquí

          // 👇 según tu respuesta real
          const regions = Array.isArray(response)
            ? response
            : response?.data || [];

          const options = regions
            .filter((region) => region.status === true)
            .map((region) => ({
              name: region.name,
              _id: region._id,
            }));

          setRegionOptions(options);
        } catch (error) {
          console.error("Error al cargar regiones:", error);
        }
      } else {
        if (payload?.regionIds) {
          setRegion(payload.regionIds);
        }
      }
    };

    loadRegions();
  }, [isAdmin, payload?.regionIds, id]);

  // 🔹 Convertir fecha a formato API
  const formatDate = (date) => {
    if (!date) return null;
    return date.toISOString().split("T")[0];
  };

  // 🔹 Buscar
  const handleSearch = () => {
    const selectedRegion = isAdmin ? [region] : payload?.regionIds;

    if (!startDate || !endDate || !selectedRegion) {
      console.warn("Faltan filtros");
      return;
    }

    fetchFoliosReport({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      regionIds: selectedRegion,
    });
  };

  // Renderizado de tabla

  const cellComponent = (item, column) => {
    switch (column) {
      case "Folio Externo":
        return item.externalFolio;
      case "Folio":
        return item.folio;

      case "Status":
        return (
          <span
            style={{
              color:
                item.status === "Finalizado"
                  ? "green"
                  : item.status === "Rechazado"
                    ? "red"
                    : "orange",
              fontWeight: "bold",
            }}
          >
            {item.status}
          </span>
        );

      case "Ajustador":
        return item.agente;

      case "Region":
        return item.region;

      case "Clasificacion":
        return item.clasificacion || "-";
      case "Comentarios":
        return item.comentarioClasificacion || "-";
      case "Tiempo de Espera":
        return formatTime(item.tiempoEspera);
      case "Tiempo de Atencion":
        return formatTime(item.tiempoAtencion);
      case "Tiempo Total":
        return formatTime(item.tiempoTotal);
      case "Fecha Creacion":
        return item.fechaCreacion
          ? new Date(item.fechaCreacion).toLocaleString()
          : "-";

      default:
        return "-";
    }
  };
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "-";

    const totalSeconds = Math.floor(seconds);

    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const pad = (n) => n.toString().padStart(2, "0");

    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  };
  // Renderizado Grafica
  const statusColors = {
    Finalizado: "#4caf50",
    Rechazado: "#f44336",
    "No tomado": "#ff9800",
    "En atención": "#2196f3",
  };

  const chartData = (() => {
    if (!data || data.length === 0) return null;

    const counts = data.reduce((acc, item) => {
      const key = item.status || "Sin status";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(counts),
      datasets: [
        {
          data: Object.values(counts),
          backgroundColor: Object.keys(counts).map(
            (status) => statusColors[status] || "#999",
          ),
        },
      ],
    };
  })();

  const handleExportCSV = () => {
    if (!data || data.length === 0) return;

    const headers = [
      "Folio Externo",
      "Folio",
      "Status",
      "Ajustador",
      "Región",
      "Clasificación",
      "Comentarios",
      "Tiempo de Espera",
      "Tiempo de Atencion",
      "Tiempo Total",
      "Fecha Creación",
    ];

    const rows = data.map((item) => [
      item.externalFolio || "",
      item.folio || "",
      item.status || "",
      item.agente || "",
      item.region || "",
      item.clasificacion || "",
      item.comentarioClasificacion || "",
      formatTime(item.tiempoEspera),
      formatTime(item.tiempoAtencion),
      formatTime(item.tiempoTotal),
      item.fechaCreacion ? new Date(item.fechaCreacion).toLocaleString() : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "reporte_folios.csv");
    document.body.appendChild(link);

    link.click();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Stack sx={{ width: "100%", maxWidth: "1400px", margin: "0 auto" }}>
      <CardComponent
        sx={{
          p: isMobile ? 2 : 4,
          width: "100%",
          height: "85vh",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Stack
          direction="column"
          width="100%"
          sx={{ overflow: "auto", flex: 1 }}
        >
          <Box
            sx={{
              width: isMobile ? "100%" : "45%",
            }}
          >
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "1fr 1fr"}
              gap={2}
              alignItems="end"
            >
              {/* 🔹 SELECT */}
              {isAdmin && (
                <Box gridColumn="1 / 2">
                  <Typography variant="caption" fontWeight="bold">
                    Selecciona la región
                  </Typography>

                  <SelectComponent
                    options={regionOptions}
                    value={region || ""}
                    onChange={(e) => setRegion(e.target.value)}
                    valueSelect="_id"
                    fontFamily="Raleway, sans-serif"
                    fontWeight={600}
                    colorLabel="#555"
                    fontSizeInput="12px"
                    fontSize="13px"
                    backgroundColor="#fff"
                    width="100%"
                  />
                </Box>
              )}

              {/* 🔹 FECHA INICIO */}
              <Box gridColumn={isAdmin ? "2 / 3" : "1 / 2"}>
                <DatePickerComponent
                  titleLabel="Fecha inicio"
                  value={startDate}
                  setValue={setStartDate}
                />
              </Box>

              {/* 🔹 FECHA FIN */}
              <Box gridColumn={isAdmin ? "2 / 3" : "1 / 2"}>
                <DatePickerComponent
                  titleLabel="Fecha fin"
                  value={endDate}
                  setValue={setEndDate}
                />
              </Box>

              {/* 🔹 BOTÓN */}
              <Box gridColumn="1 / 2">
                <ButtonComponent
                  label={"Buscar"}
                  onClick={handleSearch}
                  backgroundColor={"#73c883"}
                  disabled={!startDate || !endDate}
                  width="100%"
                />
              </Box>
            </Box>
          </Box>

          <Stack direction="row" spacing={4}>
            <Typography>Resultados: {data.length}</Typography>
          </Stack>

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Stack
              direction="column"
              spacing={2}
              width={isMobile ? "100%" : "65%"}
              sx={{ flex: 1, minHeight: 0 }}
            >
              <Typography variant="h6">
                Reporte de casos levantados a ajustador
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowX: "auto", //  scroll horizontal
                  overflowY: "auto", //  scroll vertical
                  borderRadius: "8px",
                  position: "relative",

                  // 👇 estilos mejorados del scrollbar
                  "&::-webkit-scrollbar": {
                    height: "10px",
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#999",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#666",
                    },
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f0f0f0",
                  },
                  // Para Firefox
                  scrollbarWidth: "thin",
                  scrollbarColor: "#999 #f0f0f0",
                }}
              >
                <Box sx={{ minWidth: "max-content" }}>
                  {" "}
                  {/*  se adapta al contenido */}
                  <TableLine
                    data={paginatedData}
                    columnTitles={columns}
                    cellComponent={cellComponent}
                  />
                </Box>
              </Box>

              <TablePagination
                component="div"
                count={data.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 15, 20]}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                }}
              />
              <Stack alignItems="flex-end" mt={4}>
                <ButtonComponent
                  label={"Descargar Reporte"}
                  onClick={handleExportCSV}
                  backgroundColor={"#73c883"}
                  width={isMobile ? "100%" : "25%"}
                />
              </Stack>
            </Stack>
            <Stack
              direction="column"
              spacing={2}
              width={isMobile ? "100%" : "35%"}
            >
              <Typography variant="h6">Gráficos de Reportes</Typography>

              {chartData ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  height={isMobile ? "250px" : "300px"}
                  width="100%"
                >
                  <DoughnutChart data={chartData} />
                </Stack>
              ) : (
                <Typography>No hay datos para mostrar</Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardComponent>
    </Stack>
  );
};

export default ReportesTab;
